import { computed, observable } from "mobx";
import uuid from "uuid/v4";
import { ElementAttributeType, ElementType } from "../enums";
import { Attribute, QuestionSummary } from "../models";
import { CustomValidator, Validator } from "../validators";
import { TemplateNodeVisitor } from "./ops/nodeVisitor";
import { IStyle, ITextStyle, createEmptyStyle, createEmptyTextStyle } from "./styling";

export enum NodeTag {
    Invalid = "Invalid",
    Element = "Element",
    Page = "Page",
    Form = "Form",
}

export abstract class DynamoNode {
    public abstract readonly tag: NodeTag;

    public nodeId: string = uuid();

    public styleName?: string;

    public textStyleName?: string;

    @observable public style: IStyle = createEmptyStyle();

    @observable public textStyle: ITextStyle = createEmptyTextStyle();

    @observable public text?: string;

    public abstract accept(visitor: TemplateNodeVisitor);

    public tryFindNode(nodeId: string): DynamoNode | undefined {
        if (this.nodeId === nodeId) {
            return this;
        }

        for (const child of this.getChildren()) {
            const foundMaybe = child.tryFindNode(nodeId);
            if (foundMaybe !== undefined) {
                return foundMaybe;
            }
        }
    }

    public tryFindParentOf(childNodeId: string): DynamoNode | undefined {
        for (const child of this.getChildren()) {
            if (child.nodeId === childNodeId) {
                return this;
            }

            const foundMaybe = child.tryFindParentOf(childNodeId);
            if (foundMaybe !== undefined) {
                return foundMaybe;
            }
        }
    }

    public abstract getChildren(): DynamoNode[];

    public abstract tryAddChild(child: DynamoNode, index?: number): boolean;

    public abstract tryRemoveChild(child: DynamoNode): boolean;

    public getIndexOf(node: DynamoNode): number | undefined {
        const children = this.getChildren();
        let currIndex = 0;

        for (const child of children) {
            if (child === node) {
                return currIndex;
            }

            currIndex++;
        }

        return;
    }

    public tryReorderChild(child: DynamoNode, index: number): boolean {
        const currIndex = this.getIndexOf(child);
        if (currIndex === undefined) {
            return false;
        }

        if (currIndex === index) {
            return true;
        }

        const deltaIndex = currIndex < index ? -1 : 0;

        return this.tryRemoveChild(child) &&
                this.tryAddChild(child, index + deltaIndex);
    }
}

export abstract class ElementNode extends DynamoNode {
    get hasErrors(): boolean {
        return this.errors.length > 0;
    }

    get errorText(): string {
        return this.errors.join("\n");
    }

    protected get submissionValidators(): Validator<any>[] {
        return [];
    }

    public readonly tag: NodeTag = NodeTag.Element;

    @observable public children: ElementNode[] = [];

    @observable public elementType: ElementType = ElementType.Invalid;

    @observable public attributes: Attribute<ElementAttributeType>[] = [];

    @observable public formInputName?: string;

    @observable public dynamicName?: string;

    @observable public questionSummary?: QuestionSummary;

    @observable public prototypeId?: number;

    @observable public formContextId?: number;

    @observable public parentFormContextId?: number;

    @observable public isHidden: boolean = false;

    @observable public isMultiline: boolean = false;

    @observable public summaryValue?: string;

    @observable public maxLength?: number;

    @observable public url?: string;

    @observable public dateFormat?: string;

    @observable public errors: string[] = [];

    public tryAddChild(child: DynamoNode, index?: number | undefined) {
        if (child.tag !== NodeTag.Element) {
            return false;
        }

        if (this.children.includes(child as ElementNode)) {
            return false;
        }

        if (index !== undefined) {
            this.children = [...this.children.slice(0, index), child as unknown as ElementNode, ...this.children.slice(index)];
        } else {
            this.children = [...this.children, child as unknown as ElementNode];
        }

        return true;
    }

    public tryRemoveChild(child: DynamoNode) {
        const origLength = this.children.length;
        this.children = this.children.filter((v) => v.nodeId !== child.nodeId);

        return this.children.length !== origLength;
    }

    public getChildren(): DynamoNode[] {
        return this.children.map((v) => v as DynamoNode);
    }

    public validateSubmission(shouldShortCircuit: boolean): boolean {
        const isValid = this.submissionValidators
                        .map((v) => v.validate(this))
                        .every((v) => v);

        this.errors = isValid ? [] : this.submissionValidators.flatMap((v) => v.errors);

        return isValid || !shouldShortCircuit && this.children.map((v) => v.validateSubmission(shouldShortCircuit)).every((v) => v);
    }
}

export abstract class InputNode<TInput> extends ElementNode {

    @computed get input(): TInput | undefined {
        return this._input;
    }

    set input(value: TInput | undefined) {
        this._input = value;
        this.validateSubmission(true);
    }

    get defaultValue(): TInput | undefined {
        return this._defaultValue;
    }

    set defaultValue(value: TInput | undefined) {
        this._defaultValue = value;

        if (this.input === undefined) {
            this.input = value;
        }
    }

    protected get submissionValidators(): Validator<any>[] {
        return [
            this._innerSubmissionValidators,
            ...super.submissionValidators,
        ];
    }

    @observable public requiredMessage?: string;

    @observable public isDisabled: boolean = false;

    @observable public placeholder?: string;
    @observable private _input?: TInput;

    private _defaultValue?: TInput;

    private _innerSubmissionValidators: Validator<any> = new CustomValidator<InputNode<TInput>>((node) => node !== undefined && node.isRequired() && !node.hasInput() ? "This field is required" : undefined);

    public isRequired(): boolean {
        return this.requiredMessage !== undefined;
    }

    public hasInput(): boolean {
        return this.input !== undefined;
    }
}
